import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { BigScreenTheme } from '../../lib/big-screen';
import { buildScreenModel, SLIDE_TIMINGS, type ScreenModel, type ScreenSlide } from '../../lib/big-screen-analytics';
import { advanceBatch, popupStillValid, type BatchState, type ScreenPopup } from '../../lib/big-screen-batches';
import type { PublicLeaderboard } from '../../lib/shooting';
import { ChartSlide, LeaderboardSlide, StandingsSlide } from './BigScreenSlides';
import '../../styles/big-screen-slideshow.css';

function savedTime(value: string | null): string {
  if (!value || !Number.isFinite(Date.parse(value))) return 'Waiting for scores';
  return new Date(value).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true });
}
interface FrameProps {
  model: ScreenModel; slide: ScreenSlide; theme: BigScreenTheme; batch: BatchState; isAdmin: boolean;
  onRound?: (round: 1 | 2) => void; onTheme?: (theme: BigScreenTheme) => void; themeSaving?: boolean;
  error?: string | null; missingGender?: number; disconnected?: boolean; popup?: ScreenPopup | null;
}
function NewsTicker({ items }: { items: string[] }) {
  const seconds = Math.max(35, items.join(' ').length * .19);
  const group = <>{items.map((item, i) => <span className="bs109-news-item" key={i}><i aria-hidden="true">◎</i>{item}</span>)}</>;
  return <footer className="bs109-ticker"><b className="bs109-news-label"><i />10.9 NEWS</b><div className="bs109-news-window"><div className="bs109-news-track" style={{ '--ticker-duration': `${seconds}s` } as CSSProperties}><div>{group}</div><div aria-hidden="true">{group}</div></div></div></footer>;
}
function Celebration({ popup }: { popup: ScreenPopup }) {
  return <div className={`bs109-celebration ${popup.kind}`} role="status"><div className="bs109-rays" /><div className="bs109-popup-content"><span className="bs109-popup-symbol" aria-hidden="true">{popup.kind === 'bullseye' ? '◎' : popup.kind === 'onfire' ? '🔥' : '🏆'}</span><h1>{popup.title}</h1><h2>{popup.name}</h2><p>{popup.team}</p><strong>{popup.detail}</strong></div></div>;
}
export function SlideshowFrame(props: FrameProps) {
  const { model: m, slide, batch } = props;
  return <div className={`bs109 ${props.theme}`} data-theme={props.theme} data-slide={slide} data-round={m.round}>
    <header className="bs109-header"><img className="bs109-logo" src="/assets/scoreboard/smym-logo.png?v=20261103" alt="SMYM" /><div className="bs109-org"><b>SMYM CHENNAI</b><span>Rifle shooting</span></div><div className="bs109-brand"><img src="/assets/scoreboard/rifle-109.png" alt="10.9 Rifle Shooting Tournament" /></div><span className="bs109-round">ROUND {m.round}</span>
      {props.isAdmin && <div className="bs109-admin">{props.onRound && <nav aria-label="Choose round">{([1, 2] as const).map(round => <button type="button" key={round} aria-pressed={m.round === round} onClick={() => props.onRound?.(round)}>Round {round}</button>)}</nav>}{props.onTheme && <nav aria-label="Choose theme">{(['light', 'dark'] as const).map(theme => <button type="button" key={theme} aria-pressed={props.theme === theme} disabled={props.themeSaving} onClick={() => props.onTheme?.(theme)}>{theme === 'light' ? 'Light' : 'Dark'}</button>)}</nav>}</div>}
      <div className="bs109-updated"><small>Scores updated</small><b>{savedTime(m.updatedAt)}</b><span>IST</span></div>
    </header>
    <main className="bs109-main" aria-label={`Round ${m.round} tournament slideshow`}>
      <div className={`bs109-pane ${slide === 'leaderboard' ? 'active' : ''}`} aria-hidden={slide !== 'leaderboard'}><LeaderboardSlide model={m} batch={batch} active={slide === 'leaderboard'} /></div>
      <div className={`bs109-pane ${slide === 'standings' ? 'active' : ''}`} aria-hidden={slide !== 'standings'}><StandingsSlide model={m} batch={batch} active={slide === 'standings'} /></div>
      {slide !== 'leaderboard' && slide !== 'standings' && <div className="bs109-pane active bs109-enter" key={slide}><ChartSlide slide={slide} model={m} active /></div>}
      {m.published && props.popup && <Celebration popup={props.popup} />}
    </main>
    <NewsTicker items={[...(m.published ? batch.headlines : []), ...m.ticker]} />
    {props.isAdmin && (props.error || props.missingGender) ? <p className="bs109-admin-error">{props.error ?? `${props.missingGender} shooter(s) have no gender and are omitted from Men/Women only.`}</p> : null}
    {props.disconnected && <span className="bs109-connection">Reconnecting · showing saved scores</span>}
  </div>;
}
interface SlideshowProps {
  board: PublicLeaderboard; round: 1 | 2; theme: BigScreenTheme; isAdmin: boolean;
  onRound?: (round: 1 | 2) => void; onTheme?: (theme: BigScreenTheme) => void; themeSaving?: boolean; error?: string | null; connected: boolean;
}
/** Mounted separately per tournament/round: a newly opened round is a silent baseline. */
export function BigScreenSlideshow(props: SlideshowProps) {
  const model = useMemo(() => buildScreenModel(props.board, props.round), [props.board, props.round]);
  const [presented, setPresented] = useState(model);
  const [slide, setSlide] = useState<ScreenSlide>('leaderboard');
  const [visit, setVisit] = useState(0);
  const [batch, setBatch] = useState(() => advanceBatch(null, model));
  const previous = useRef(batch);
  const initial = useRef(model);
  const latestModel = useRef(model); latestModel.current = model;
  const refreshTimer = useRef<number | null>(null);
  const wasPublished = useRef(model.published);
  const [popups, setPopups] = useState<ScreenPopup[]>([]);
  const deferred = useRef(new Set<number>());

  useEffect(() => {
    if (initial.current === model) return;
    if (wasPublished.current !== model.published) {
      if (refreshTimer.current != null) window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
      wasPublished.current = model.published;
      setPresented(model);
      deferred.current.forEach(window.clearTimeout); deferred.current.clear(); setPopups([]);
      setSlide('leaderboard'); setVisit(n => n + 1);
      const seed = advanceBatch(null, model); previous.current = seed; setBatch(seed);
      return;
    }
    wasPublished.current = model.published;
    if (refreshTimer.current != null) return;
    // A bounded window, rather than an idle-only debounce: continuous saves must still refresh.
    refreshTimer.current = window.setTimeout(() => {
      refreshTimer.current = null;
      const current = latestModel.current;
      const next = advanceBatch(previous.current, current);
      if (next.invalidated) {
        deferred.current.forEach(window.clearTimeout); deferred.current.clear(); setPopups([]);
      } else setPopups(queue => queue.filter(popup => popupStillValid(popup, current)));
      // Commit the new order together with its batch revision, preserving old FLIP positions.
      setPresented(current); previous.current = next; setBatch(next);
      if (!next.event) return;
      setSlide(current.latestTeams.length >= 2 ? 'duo' : 'leaderboard'); setVisit(n => n + 1);
      const id = window.setTimeout(() => { deferred.current.delete(id); setPopups(queue => [...queue, ...next.event!.popups.filter(popup => popupStillValid(popup, latestModel.current))].slice(0, 8)); }, 2_000);
      deferred.current.add(id);
    }, SLIDE_TIMINGS.batchSettle);
  }, [model]);
  useEffect(() => () => {
    if (refreshTimer.current != null) window.clearTimeout(refreshTimer.current);
    refreshTimer.current = null;
    deferred.current.forEach(window.clearTimeout); deferred.current.clear();
  }, []);
  const available = presented.slides.join(',');
  useEffect(() => {
    if (!presented.slides.includes(slide)) { setSlide('leaderboard'); return; }
    const ms = slide === 'leaderboard' ? SLIDE_TIMINGS.leaderboard : slide === 'duo' ? SLIDE_TIMINGS.duo : SLIDE_TIMINGS.chart;
    const timer = window.setTimeout(() => setSlide(presented.slides[(presented.slides.indexOf(slide) + 1) % presented.slides.length]), ms);
    return () => window.clearTimeout(timer);
  }, [slide, available, visit]);
  const currentPopup = popups[0] ?? null;
  useEffect(() => {
    if (!currentPopup) return;
    const timer = window.setTimeout(() => setPopups(queue => queue.slice(1)), SLIDE_TIMINGS.popup);
    return () => window.clearTimeout(timer);
  }, [currentPopup]);
  return <SlideshowFrame model={model.published ? presented : model} slide={slide} batch={batch} theme={props.theme} isAdmin={props.isAdmin} onRound={props.onRound} onTheme={props.onTheme} themeSaving={props.themeSaving} error={props.error} missingGender={props.board.bigScreen?.missingGender} disconnected={!props.connected} popup={currentPopup} />;
}
